import * as React from "react";
import Icon from "../Icon/";
import LinkComp from "../LinkComp";
import svgBuyers from "../../svgs/Buyers.svg";
import svgStats from "../../svgs/Stats.svg";
import svgProperties from "../../svgs/Properties.svg";
import svgSellers from "../../svgs/Sellers.svg";
import svgAgents from "../../svgs/Agents.svg";
import svgFirms from "../../svgs/Firms.svg";
import svgTenants from "../../svgs/Tenants.svg";
import svgLenders from "../../svgs/Lenders.svg";
import svgUploads from "../../svgs/Upload.svg";
import svgListingSources from "../../svgs/ListingSources.svg";
import svgOwnerResearch from "../../svgs/OwnerResearch.svg";
import svgAccessLevels from "../../svgs/AccessLevels.svg";
import svgSignout from "../../svgs/Signout.svg";
import svgExpand from "../../svgs/Expand.svg";
import svgCollapse from "../../svgs/collapse.svg";
import svgSummitPipeline from "../../svgs/StrivePipeline.svg";
import svgAgentCommissionBreakdown from "../../svgs/AgentCommissionBreakout.svg";
import svgAgentPipeline from "../../svgs/AgentPipeline.svg";
import svgBuckets from "../../svgs/Buckets.svg";
import svgInventory from "../../svgs/Inventory.svg";
import svgEscrowTracking from "../../svgs/EscrowTracking.svg";
import svgPipeline from "../../svgs/Pipeline.svg";
import svgSummitFees from "../../svgs/Fees.svg";
import svgHscPipeline from "../../svgs/HscPipeline.svg";
import svgHscTracking from "../../svgs/HSCTracking.svg";
import svgTrainingPages from "../../svgs/TrainingPages.svg";
import plus from '../../svgs/plus-dark.svg';
import PipelineDropdown from "../PipelineDropdown/PipelineDropdown";
import svgHsc from "../../svgs/HSC.svg";
import svgTeamMembers from "../../svgs/TeamMembers.svg";
import svgVendors from "../../svgs/Vendor.svg";
import svgHscFollowUp from "../../svgs/hscFollowUp.svg"
import proposal from "../../svgs/proposal.svg";
import svgChevron from "../../svgs/chevron-right.svg";
import {can} from "../../access";
import {canOpenRoute, canOpenTeamMember, SUMMIT_PIPELINE_LINK_ROUTES} from "../../route-access";

// Improved IA: Links grouped by semantic purpose. Each link is shown when its
// `route` opens (route-access.js); its '+' when `newRoute` opens.
const getDataEntityLinks = () => [
    {
        name: "Properties",
        listPath: "list",
        route: "list",
        newPath: "property",
        newRoute: "property",
        svg: svgProperties,
    }, {
        name: "Firms",
        listPath: "firms",
        route: "firms",
        newPath: "firm",
        newRoute: "firm",
        svg: svgFirms,
    }, {
        name: "Investors",
        listPath: "investors",
        route: "investors",
        newPath: "investor",
        newRoute: "investor",
        svg: svgBuyers,
    }, {
        name: "Agents",
        listPath: "agents",
        route: "agents",
        newPath: "agent",
        newRoute: "agent",
        svg: svgAgents,
    }, {
        name: "Vendors",
        listPath: "vendors",
        route: "vendors",
        newPath: "vendors",
        newRoute: "vendor",
        svg: svgVendors,
    }, {
        name: "Offices",
        listPath: "offices",
        route: "offices",
        newPath: null,
        svg: svgFirms,
    },
];

// Proposals has no '+': there is no route for a new proposal.
const getAnalyticsLinks = () => [
    {
        name: "Proposals",
        listPath: "proposals",
        route: "proposals",
        newPath: null,
        svg: proposal,
    },
];

const getOperationsLinks = (userData) => {
    const ownId = userData && userData.id;
    // Without the team list a member has only their own record, for their Forms.
    const teamLink = can('team.list')
        ? {
            name: "Team",
            listPath: "teamMembers?order=LAST_NAME.desc",
            route: "teamMembers",
            // Adding a member sets their login, which is account settings.
            newPath: "teamMember",
            newRoute: "teamMember",
            svg: svgTeamMembers,
        }
        : {
            name: "Team",
            listPath: `teamMember/${ownId}`,
            ownRecordId: ownId,
            svg: svgTeamMembers,
        };
    return [teamLink, {
        name: "Access Levels",
        listPath: "access-levels",
        route: "access-levels",
        svg: svgAccessLevels,
    }, {
        name: "Training",
        listPath: "training",
        route: "training",
        svg: svgTrainingPages,
    }, {
        name: "Uploads",
        listPath: "upload-file",
        route: "upload-file",
        svg: svgUploads,
    }, {
        // Without an entry here the screen is reachable only by typing the
        // URL, which is how the parcel map ended up undiscoverable.
        name: "Listing Sources",
        listPath: "listing-sources",
        route: "listing-sources",
        svg: svgListingSources,
    }, {
        name: "True Owners",
        listPath: "true-owners",
        route: "true-owners",
        svg: svgListingSources,
    }, {
        name: "Owner Research",
        listPath: "owner-research",
        route: "owner-research",
        svg: svgOwnerResearch,
    }];
};

/** A link is offered exactly when its page would open. */
const linkOpens = (link) => (link.ownRecordId !== undefined
    ? canOpenTeamMember(can, link.ownRecordId, link.ownRecordId)
    : canOpenRoute(can, link.route));

// `route` (or `ownRecordId`) is what decides the link: it shows when its route opens (route-access.js).
const getSummitPipelineLinks = (paidProcuredAgentId) => [
    {
        key: "agent-commission-breakdown",
        url: "agent-commission-breakdown",
        name: "Agent Commission",
        svg: svgAgentCommissionBreakdown,
    },
    {
        key: "paid-procured",
        url: `agent-commission-breakdown/paid-procured/${new Date().getFullYear()}/${paidProcuredAgentId}/paid`,
        name: "Paid and Procured",
        svg: svgLenders,
    },
    {
        key: "agent-pipeline",
        url: "agent-pipeline",
        name: "Agent Pipeline",
        svg: svgAgentPipeline,
    }, {
        key: "buckets",
        url: "buckets",
        name: "Buckets",
        svg: svgBuckets,
    }, {
        key: "inventory",
        url: "inventory",
        name: "Inventory",
        svg: svgInventory,
    }, {
        key: "escrow-tracking",
        url: "escrow-tracking",
        name: "Escrow Tracking",
        svg: svgEscrowTracking,
    }, {
        key: "summit-fees",
        url: "summit-fees",
        name: "SUMMIT Fees",
        svg: svgSummitFees,
    },
];

const hscPipelineLinks = [
    {
        url: "pipeline",
        name: "Pipeline",
        svg: svgHscPipeline,
    }, {
        url: "tracking",
        name: "Tracking",
        svg: svgHscTracking,
    },{
        url: "stats",
        name: "Stats",
        svg: svgStats,
    },
    {
        url: "hotlist",
        name: "Hot List",
        className: "hotlist",
        svg: svgHscFollowUp,
    },
]

const getPeakPipelineLinks = () => [
    {
        // Route is still `target-list`: the view was named Target List before it
        // became Pipeline, and `pipeline` was already taken by the page below
        // (now "Closed"). Renaming the path would break anyone's saved link.
        url: "target-list",
        name: "Pipeline",
        svg: svgInventory,
    }, {
        url: "tracking",
        name: "Tracking",
        svg: svgHscTracking,
    }, {
        // The original Peak "Pipeline" page; renamed to what it actually
        // reports on now that the new Pipeline view carries that name.
        url: "pipeline",
        name: "Closed",
        svg: svgHscPipeline,
    }, {
        url: "stats",
        name: "Peak Stats",
        svg: svgStats,
    },
].filter((link) => canOpenRoute(can, link.url))

export default class Sidebar extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            isOpen: false,
            isOpenSummitPipeline: false,
            isOpenHscPipeline: false,
        };
        this.setState = this.setState.bind(this);
        this.handleCollapse = this.handleCollapse.bind(this);
        this.handleCollapseSummitPipeline = this.handleCollapseSummitPipeline.bind(this);
        this.handleCollapseHscPipeline = this.handleCollapseHscPipeline.bind(this);
        this.onLinkHover = this.onLinkHover.bind(this);
    }

    handleCollapse() {
        this.setState((prevState) => ({isOpen: !prevState.isOpen}));
    }

    handleCollapseSummitPipeline() {
        this.setState((prevState) => ({
            isOpenSummitPipeline: !prevState.isOpenSummitPipeline,
        }));
    }

    handleCollapseHscPipeline() {
        this.setState((prevState) => ({
            isOpenHscPipeline: !prevState.isOpenHscPipeline,
        }));
    }

    onLinkHover(e) {
        if (!this.state.isOpen) {
            let menuItem = e.currentTarget;
            let wrapper = menuItem.querySelector("div");
            let tooltip = wrapper.children[1];
            tooltip.style.top = menuItem.getBoundingClientRect().top + "px";
        }
    }

    renderMenuSection(links, sectionTitle, path, isOpen) {
        const hideNewButton = ['firms', 'investors', 'agents', 'vendors'];
        const visible = links.filter(linkOpens);
        if (visible.length === 0) return null;
        return (
            <React.Fragment>
                {isOpen && sectionTitle && (
                    <li className="Sidebar_section-header">
                        <div className="Sidebar_section-header_text">{sectionTitle}</div>
                    </li>
                )}
                {visible.map((x, i) => (
                    <li className="Sidebar_item"
                        key={`${sectionTitle}-${i}`}
                        onMouseOver={this.onLinkHover}>
                        <div className="item-wrapper">
                            <LinkComp className={`Sidebar_item_link ${isOpen ? "" : "closed-sidebar"}` +
                                        ([x.listPath.includes('?') ? x.listPath.split('?')[0] : x.listPath, x.newPath].includes(path)
                                            ? " Sidebar_item_link--active" : "")}
                                       to={arc.path.base + x.listPath}>
                                <Icon svg={x.svg}/>
                                <div className="Sidebar_item_link_name">{x.name}</div>
                            </LinkComp>
                            {isOpen ? null : (<span className="tooltip">{x.name}</span>)}
                            {x.newPath && !hideNewButton.includes(x.listPath) && canOpenRoute(can, x.newRoute) &&
                                <LinkComp className="New-button"
                                          to={arc.path.base + x.newPath}>
                                    <Icon svg={plus}/>
                                </LinkComp>
                            }
                        </div>
                    </li>
                ))}
            </React.Fragment>
        );
    }

    render() {
        const {signOut, pathname, userData} = this.props;
        const dataEntityLinks = getDataEntityLinks();
        const analyticsLinks = getAnalyticsLinks();
        const operationsLinks = getOperationsLinks(userData);
        const peakPipelineLinks = getPeakPipelineLinks();
        const {isOpen} = this.state;
        // Someone who sees only their own commissions gets their own Paid and
        // Procured; everyone else starts on agent 1 and picks from the list.
        const ownCommission = !can('summit.commission', 'all');
        const summitLinksVisible = getSummitPipelineLinks(ownCommission && userData ? userData.id : 1)
            .filter((link) => canOpenRoute(can, SUMMIT_PIPELINE_LINK_ROUTES[link.key]));
        const isLinkAgentCommission = pathname.includes('agent-commission-breakdown') && pathname.split('/').length > 2;
        const path = !isLinkAgentCommission ? (pathname.includes('/') ? pathname.split('/')[1] : pathname) :(pathname.replace('/', ''));

        return signOut ?
            (<div className="Body-wrapper">
                <div className={`Sidebar ${isOpen ? "open" : "closed"}`}>
                    {isOpen && (
                        <div className="Sidebar_logo">
                            <img 
                                src="/img/light-logo.png" 
                                alt='Summit Logo' 
                                style={{ 
                                    width: '100%', 
                                    height: 'auto', 
                                    display: 'block',
                                    padding: '20px'
                                }}
                            />
                        </div>
                    )}
                    <div className="Sidebar_collapse-btn" onClick={this.handleCollapse}>
                        <Icon svg={isOpen ? svgCollapse : svgExpand}/>
                        {isOpen ? null : <span className="tooltip collapse-tooltip">Expand menu</span>}
                    </div>
                    <ul className="Sidebar_list">
                        {canOpenRoute(can, '') && 
                            <li className="Sidebar_item" onMouseOver={this.onLinkHover}>
                                <div className="item-wrapper">
                                    <LinkComp className={"Sidebar_item_link" + (arc.path.base === pathname ? " Sidebar_item_link--active" : "")}
                                        to={arc.path.base}>
                                        <Icon svg={svgStats} />
                                        <div className="Sidebar_item_link_name">Stats Dashboard</div>
                                    </LinkComp>
                                    {isOpen ? null : <span className="tooltip">Stats Dashboard</span>}
                                </div>
                            </li>
                        }
                        
                        {/* DATA & ENTITIES Section */}
                        {this.renderMenuSection(dataEntityLinks, "DATA & ENTITIES", path, isOpen)}
                        
                        {/* SUMMIT PIPELINE: a link shows when its route opens (route-access.js) */}
                        {summitLinksVisible.length > 0 && (
                            <React.Fragment>
                                {isOpen && (
                                    <li className="Sidebar_section-header">
                                        <div className="Sidebar_section-header_text">SUMMIT PIPELINE</div>
                                    </li>
                                )}
                                {summitLinksVisible.map((link, i) => (
                                    <li className="Sidebar_item"
                                        key={`summit-pipeline-${i}`}
                                        onMouseOver={this.onLinkHover}>
                                        <div className="item-wrapper">
                                            <LinkComp
                                                className={`Sidebar_item_link ${isOpen ? "" : "closed-sidebar"}${link.url === path ? " Sidebar_item_link--active" : ""}`}
                                                to={arc.path.base + link.url}>
                                                <Icon svg={link.svg} className={link.className} />
                                                <div className="Sidebar_item_link_name">{link.name}</div>
                                            </LinkComp>
                                            {isOpen ? null : <span className="tooltip">{link.name}</span>}
                                        </div>
                                    </li>
                                ))}
                            </React.Fragment>
                        )}
                        
                        {/* ANALYTICS & REPORTS Section - hidden when there are no links to render
                            (e.g. Proposals is gated on its route) */}
                        {this.renderMenuSection(analyticsLinks, "ANALYTICS & REPORTS", path, isOpen)}

                        {/* PEAK PIPELINE Section */}
                        {peakPipelineLinks.length > 0 && (
                            <React.Fragment>
                                {isOpen && (
                                    <li className="Sidebar_section-header">
                                        <div className="Sidebar_section-header_text">PEAK PIPELINE</div>
                                    </li>
                                )}
                                {peakPipelineLinks.map((link, i) => (
                                    <li className="Sidebar_item"
                                        key={`peak-pipeline-${i}`}
                                        onMouseOver={this.onLinkHover}>
                                        <div className="item-wrapper">
                                            <LinkComp
                                                className={`Sidebar_item_link ${isOpen ? "" : "closed-sidebar"}${link.url === path ? " Sidebar_item_link--active" : ""}`}
                                                to={arc.path.base + link.url}>
                                                <Icon svg={link.svg} className={link.className} />
                                                <div className="Sidebar_item_link_name">{link.name}</div>
                                            </LinkComp>
                                            {isOpen ? null : <span className="tooltip">{link.name}</span>}
                                        </div>
                                    </li>
                                ))}
                            </React.Fragment>
                        )}
                        
                        {/* OPERATIONS Section */}
                        {this.renderMenuSection(operationsLinks, "OPERATIONS", path, isOpen)}
                        
                        {/* ACCOUNT Section */}
                        {isOpen && (
                            <li className="Sidebar_section-header">
                                <div className="Sidebar_section-header_text">ACCOUNT</div>
                            </li>
                        )}
                        <li className="Sidebar_item signout" onMouseOver={this.onLinkHover}>
                            <div className="item-wrapper">
                                <LinkComp className="Sidebar_item_link"
                                          to={arc.path.base}
                                          onClick={signOut}>
                                    <Icon svg={svgSignout}/>
                                    <div className="Sidebar_item_link_name">Sign out</div>
                                </LinkComp>
                                {isOpen ? null : <span className="tooltip">Sign out</span>}
                            </div>
                        </li>
                    </ul>
                </div>
                <div className={`Body-wrapper_content ${isOpen ? "wrapper-open" : "wrapper-closed"}`}>
                    {this.props.children}
                </div>
            </div>)
            :
            (this.props.children);
    }
}
